import { useRef, useEffect, useCallback } from "react";

/**
 * Custom hook to create a stable callback reference that always calls the latest version
 * of the provided function. Useful for avoiding unnecessary effect dependencies.
 * 
 * @param callback - The callback function to stabilize
 * @returns A stable callback reference that won't cause re-renders
 * 
 * @example
 * const fetchData = useStableCallback(async () => {
 *   const data = await api.fetchData();
 *   setData(data);
 * });
 * 
 * useEffect(() => {
 *   fetchData(); // Won't cause infinite loops
 * }, [fetchData]);
 */
export function useStableCallback<T extends (...args: any[]) => any>(
  callback: T
): T {
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  return useCallback((...args: Parameters<T>) => {
    return callbackRef.current(...args);
  }, []) as T;
}

/**
 * Custom hook to create multiple stable callback references
 * 
 * @param callbacks - Object of callbacks to stabilize
 * @returns Object with the same keys but stabilized callbacks
 * 
 * @example
 * const { fetchData, deleteData } = useStableCallbacks({
 *   fetchData: async () => await api.fetch(),
 *   deleteData: async (id) => await api.delete(id)
 * });
 */
export function useStableCallbacks<T extends Record<string, (...args: any[]) => any>>(
  callbacks: T
): T {
  const callbacksRef = useRef(callbacks);

  useEffect(() => {
    callbacksRef.current = callbacks;
  }, [callbacks]);

  const stableCallbacks = useRef<T>({} as T);

  if (Object.keys(stableCallbacks.current).length === 0) {
    Object.keys(callbacks).forEach((key) => {
      stableCallbacks.current[key as keyof T] = ((...args: any[]) => {
        return callbacksRef.current[key](...args);
      }) as T[keyof T];
    });
  }

  return stableCallbacks.current;
}
